import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import WatchIcon from "@mui/icons-material/Watch";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PageHeader from "../../components/PageHeader";

const maroon = "#500000";

const IDENTIFIED_DATA_FORM_URL =
  process.env.REACT_APP_IDENTIFIED_DATA_FORM_URL || "";

const handleRequestIdentifiedData = () => {
  if (!IDENTIFIED_DATA_FORM_URL) {
    console.error(
      "REACT_APP_IDENTIFIED_DATA_FORM_URL is not configured."
    );
    return;
  }
  window.open(IDENTIFIED_DATA_FORM_URL, "_blank", "noopener,noreferrer");
};

function DataSourceCard({ icon, title, question, buttonLabel, color, onSelect }) {
  return (
    <Card
      variant="outlined"
      onClick={onSelect}
      sx={{
        borderRadius: 3,
        height: "100%",
        cursor: "pointer",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(80, 0, 0, 0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 4,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Avatar sx={{ bgcolor: `${color}22`, color, width: 56, height: 56, mb: 2 }}>
          {icon}
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
          {question}
        </Typography>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          sx={{
            backgroundColor: maroon,
            borderRadius: "20px",
            px: 3,
            "&:hover": { backgroundColor: "#4a0000" },
          }}
        >
          {buttonLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

const ResearcherDashboard = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Researcher Dashboard"
        subtitle="Choose a data source to explore"
        actions={
          <Button
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={handleRequestIdentifiedData}
            sx={{
              borderColor: maroon,
              color: maroon,
              borderRadius: "20px",
              px: 3,
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#4a0000",
                backgroundColor: "rgba(80, 0, 0, 0.06)",
              },
            }}
          >
            Request Identified Data
          </Button>
        }
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataSourceCard
            icon={<StorageIcon />}
            title="Biobank Data"
            question="Are you interested in our Biobank data?"
            buttonLabel="View Dashboard"
            color="#2F6F6B"
            onSelect={() => navigate("/researcher/biobank-dashboard")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataSourceCard
            icon={<LocalHospitalIcon />}
            title="EHR Data"
            question="Are you interested in our EHR data?"
            buttonLabel="Go to Power BI"
            color="#E0645A"
            onSelect={() => navigate("/researcher/reports")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataSourceCard
            icon={<WatchIcon />}
            title="Wearable Device Data"
            question="Are you interested in our wearable device data?"
            buttonLabel="View Dashboard"
            color="#7C6FDB"
            onSelect={() => navigate("/researcher/wearable-dashboard")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataSourceCard
            icon={<AssignmentIcon />}
            title="REDCap Data"
            question="Are you interested in our survey data?"
            buttonLabel="View Dashboard"
            color="#E8A33D"
            onSelect={() => navigate("/researcher/redcap-dashboard")}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResearcherDashboard;